/**
 * Caption above a device — the layout most App Store sets are built on.
 *
 * The headline sits in the top band and the device fills everything below it, fitted rather than
 * scaled by hand, so the same template works on an iPhone, an iPad and both Duo displays.
 */
import { Template } from '@keymaker/schema';
import { bandBelow, fittedDevice, gradient, radial, text } from '../kit';

const HEADLINE = { x: 0.045, y: 0.035, width: 0.91, height: 0.14 };
const HEADLINE_SMALL = { x: 0.045, y: 0.035, width: 0.91, height: 0.09 };
const KICKER = { x: 0.045, y: 0.135, width: 0.91, height: 0.05 };

export const headlineDevice = Template.parse({
  id: 'headline-device',
  name: 'Headline above device',
  category: 'caption-top',
  description: 'A headline in the top band and the device filling the rest. The default first panel.',
  tags: ['verdict', 'feature', 'default'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#134DFF', '#2B14A0']),
    layers: [
      text({ content: '{{caption.1}}', box: HEADLINE, size: 0.041 }),
      fittedDevice({ top: bandBelow(HEADLINE) }),
    ],
  },
});

export const headlineDeviceDark = Template.parse({
  id: 'headline-device-dark',
  name: 'Headline above device (dark glow)',
  category: 'caption-top',
  description: 'A soft glow behind the device on a near-black canvas, so the bezel reads against it.',
  tags: ['verdict', 'dark', 'premium'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: radial([
      { offset: 0, color: '#243065' },
      { offset: 0.55, color: '#121732' },
      { offset: 1, color: '#080A16' },
    ]),
    layers: [
      text({ content: '{{caption.1}}', box: HEADLINE, size: 0.041 }),
      fittedDevice({
        top: bandBelow(HEADLINE),
        shadow: { blur: 0.05, opacity: 0.5, offsetY: 0.01, color: '#000000' },
      }),
    ],
  },
});

export const headlineKickerDevice = Template.parse({
  id: 'headline-kicker-device',
  name: 'Headline, kicker, device',
  category: 'caption-top',
  description: 'A two-line headline with a supporting line beneath it, then the device.',
  tags: ['feature', 'explain'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#0F172E', '#2B14A0']),
    layers: [
      text({ content: '{{caption.1}}', box: HEADLINE_SMALL, size: 0.041 }),
      text({
        id: 'kicker',
        content: '{{caption.2}}',
        box: KICKER,
        size: 0.021,
        weight: 400,
        color: '#C3CCFF',
        lineHeight: 1.25,
      }),
      fittedDevice({ top: bandBelow(KICKER) }),
    ],
  },
});

export const captionTopTemplates = [headlineDevice, headlineDeviceDark, headlineKickerDevice];
